import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, inGameFilter, Tabs, ScrollRow, SectionLabel, VerdictChip, Avatar, tdColor, TeamLogo, TeamTag, PosFilter, POS_GROUPS, posOf } from '../ui'
import { getNFLTdRoles } from '../api'
import { teamNick } from '../plain'

// TD MATCHUPS — written for a regular fan, not an analyst. The question it answers: "who's likely
// to score this week, and is his matchup helping or hurting?" Under the hood: TDs each defense
// allows to a ROLE (top WR, #2 WR, lead RB, QB runs…) vs the league rate for that role, shrunk
// toward average for small samples (backend/nfl/features/td_roles.py). The screen leads with the
// model's chance to score and a one-word matchup grade; the numbers sit behind "How this works".
// Research only — not a wager.

const ROLE = { WR1: 'top WR', WR2: '#2 WR', WR3: '#3 WR', WR_OTHER: 'other WRs', TE1: 'top TE', TE2: '#2 TE',
  TE_OTHER: 'other TEs', RB1: 'lead RB', RB2: 'backup RBs', QB: 'QB runs' }
const ROLE_PLURAL = { WR1: 'top WRs', WR2: '#2 WRs', WR3: '#3 WRs', WR_OTHER: 'other WRs', TE1: 'top TEs',
  TE2: '#2 TEs', TE_OTHER: 'other TEs', RB1: 'lead RBs', RB2: 'backup RBs', QB: 'QB runs' }
const GROUPS = [['WR', 'Receivers'], ['TE', 'Tight ends'], ['RB', 'Running backs'], ['QB', 'QB runs']]
const ord = n => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]) }

// One-word matchup grade from the defense's softness vs his role. rank 1 = gives up the most.
export function grade(rank, mult) {
  if (mult == null) return { w: 'Average', c: T.muted, lvl: 'dead' }
  if (rank <= 8 && mult >= 1.2) return { w: 'Great', c: T.green, lvl: 'strong' }
  if (mult >= 1.05) return { w: 'Good', c: T.mint, lvl: 'good' }
  if (rank >= 25 && mult <= 0.8) return { w: 'Tough', c: T.red, lvl: 'warn' }
  if (mult <= 0.9) return { w: 'Below avg', c: T.amber, lvl: 'neutral' }
  return { w: 'Average', c: T.muted2, lvl: 'dead' }
}
const times = m => `${m.toFixed(1)}×`

function why(p) {
  const r = ROLE_PLURAL[p.role] || p.role_label
  const t = `The ${teamNick(p.opp)}`
  if (p.opp_mult == null) return `${t} haven't given up a TD to ${r} yet — too early to read.`
  if (p.opp_mult >= 1.05) return `${t} allow the ${ord(p.opp_rank)}-most TDs to ${r} (${times(p.opp_mult)} average).`
  if (p.opp_mult <= 0.95) return `${t} are stingy vs ${r} — ${ord(p.opp_rank)} of ${p.n} (${times(p.opp_mult)} average).`
  return `${t} are about average vs ${r}.`
}

function SpotRow({ p, onPlayer }) {
  const pct = p.model_td_prob != null ? Math.round(p.model_td_prob * 100) : null
  const g = grade(p.opp_rank, p.opp_mult)
  return (
    <button onClick={() => onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, pos: p.pos,
      team: p.team, opp: p.opp, headshot: p.headshot, prop_type: 'anytime_td', status: 'active' })}
      style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', cursor: 'pointer',
        background: T.card, border: `1px solid ${g.w === 'Great' ? T.greenMid : T.border}`, borderRadius: 11,
        padding: '11px 13px', marginBottom: 8, color: T.text }}>
      <Avatar url={p.headshot} name={p.name} team={p.team} size={40} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 700 }}>{teamNick(p.team)} {ROLE[p.role]} · vs {teamNick(p.opp)}</span>
          <VerdictChip label={`${g.w.toUpperCase()} MATCHUP`} level={g.lvl} />
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: T.muted2, marginTop: 4, lineHeight: 1.35 }}>{why(p)}</span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0, minWidth: 52 }}>
        <span style={{ display: 'block', fontFamily: T.mono, fontSize: 21, fontWeight: 900,
          color: pct != null ? tdColor(pct / 100) : T.muted }}>{pct != null ? `${pct}%` : '—'}</span>
        <span style={{ display: 'block', fontSize: 10, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>to score</span>
      </span>
    </button>
  )
}

function RoleLine({ role, c }) {
  // Zero TDs allowed → say so; with nothing allowed, the role's rank is just a shuffle of ties.
  const g = c.tds === 0 ? { w: 'None yet', c: T.muted } : grade(c.rank, c.mult)
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: T.text, textTransform: 'capitalize' }}>{ROLE_PLURAL[role]}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 11, color: T.muted }}>{c.tds} TD{c.tds === 1 ? '' : 's'} allowed</span>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: g.c, minWidth: 74, textAlign: 'right' }}>
          {g.w}{g.w === 'Great' ? <span style={{ color: T.faint, fontWeight: 700 }}> · {ord(c.rank)}-most</span> : null}</span>
      </span>
    </div>
  )
}

function DefenseView({ d, n }) {
  const all = Object.values(d.groups).flatMap(g => Object.entries(g.roles))
    .filter(([r, c]) => c.mult != null && !r.endsWith('OTHER'))
  const easy = all.filter(([, c]) => grade(c.rank, c.mult).w === 'Great').sort((a, b) => a[1].rank - b[1].rank)
  const hard = all.filter(([, c]) => grade(c.rank, c.mult).w === 'Tough')
  const list = xs => xs.map(([r]) => ROLE_PLURAL[r]).join(', ')
  return (
    <div>
      <div style={{ margin: '4px 0 12px' }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}><TeamTag abbr={d.team} size={26} /> defense <span style={{ fontSize: 13, color: T.muted, fontWeight: 700 }}>vs the {teamNick(d.faces)} this week</span></div>
        <div style={{ fontSize: 13, color: T.muted2, marginTop: 6, lineHeight: 1.5 }}>
          {easy.length ? <>Easiest to score on: <b style={{ color: T.green }}>{list(easy)}</b>. </> : <>No standout weak spot. </>}
          {hard.length ? <>Hardest: <b style={{ color: T.red }}>{list(hard)}</b>.</> : null}
        </div>
        <div style={{ fontSize: 11, color: T.faint, marginTop: 4 }}>Overall: {ord(d.composite.rank)}-most TDs allowed of {n} · last {d.games} games</div>
      </div>
      {GROUPS.map(([g, label]) => {
        const grp = d.groups[g]
        const gg = grade(grp.rank, grp.mult)
        const roles = Object.entries(grp.roles).filter(([r]) => !r.endsWith('OTHER'))
        return (
          <div key={g} style={{ background: gg.w === 'Great' ? T.greenDim : T.card,
            border: `1px solid ${gg.w === 'Great' ? T.green : T.border}`, borderRadius: 11, padding: '10px 13px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: T.text }}>{label}</span>
              <VerdictChip label={`${gg.w.toUpperCase()} MATCHUP`} level={gg.lvl} />
            </div>
            {roles.length > 1 && <div style={{ marginTop: 4, borderTop: `1px solid ${T.border}` }}>
              {roles.map(([r, c]) => <RoleLine key={r} role={r} c={c} />)}</div>}
            {roles.length === 1 && <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{grp.tds} TD{grp.tds === 1 ? '' : 's'} allowed in {d.games} games</div>}
          </div>
        )
      })}
    </div>
  )
}

function HowItWorks({ d }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ marginTop: 14 }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: T.green, fontSize: 12, fontWeight: 800 }}>{open ? '▾' : '▸'} How this works</button>
      {open && <div style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.6, marginTop: 8 }}>
        <p style={{ margin: '0 0 8px' }}><b style={{ color: T.text }}>Roles</b> come from who actually gets the ball this season — the
          receiver with the most targets per game is the "top WR", the back with the most touches is the "lead RB".</p>
        <p style={{ margin: '0 0 8px' }}><b style={{ color: T.text }}>Matchup</b> compares how many TDs a defense has allowed to that role
          against the league average. <b style={{ color: T.green }}>Great</b> = a top-8 soft spot at 1.2× average or more;
          {' '}<b style={{ color: T.red }}>Tough</b> = one of the stingiest. Early-season numbers are pulled toward average
          ({d.shrink_games || 4} games' worth) so a lucky streak doesn't look like a sure thing.</p>
        <p style={{ margin: 0 }}><b style={{ color: T.text }}>Chance to score</b> is our model's anytime-TD probability — it already
          weighs the matchup, usage, red-zone role and injuries. The matchup is the "why"; the % is the number we stand behind.
          {d.carryover ? ` Early season: using ${d.source_season} data.` : ''} Informational only · 21+.</p>
      </div>}
    </div>
  )
}

export default function TdRolesTab({ season = 2026, week = 1, onPlayer, gameFilter }) {
  const [d, setD] = useState(null)
  const [mode, setMode] = useState('Players')
  const [team, setTeam] = useState(null)
  const [pos, setPos] = useState('ALL')
  useEffect(() => { let a = true; setD(null)
    getNFLTdRoles(season, week).then(r => { if (a) setD(r) }); return () => { a = false } }, [season, week])

  if (!d) return <Spinner />
  const n = d.n_defenses || 32
  const defs = (d.defenses || []).filter(x => x.faces && inGameFilter(gameFilter, x.team))
  const byChance = (a, b) => (b.model_td_prob ?? -1) - (a.model_td_prob ?? -1) || b.adj_td_pg - a.adj_td_pg
  const all = (d.players || []).filter(p => inGameFilter(gameFilter, p.team)).map(p => ({ ...p, n }))
  const counts = Object.fromEntries(POS_GROUPS.map(g => [g, g === 'ALL' ? all.length : all.filter(p => posOf(p) === g).length]))
  const spots = pos === 'ALL' ? all : all.filter(p => posOf(p) === pos)
  const great = spots.filter(p => grade(p.opp_rank, p.opp_mult).w === 'Great').sort(byChance)
  const rest = spots.filter(p => grade(p.opp_rank, p.opp_mult).w !== 'Great').sort(byChance)
  const sel = defs.find(x => x.team === team) || defs[0]
  return (
    <div style={{ padding: '10px 14px 80px' }}>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.green}`,
        borderRadius: 11, padding: '11px 13px', marginBottom: 12, fontSize: 12.5, color: T.muted2, lineHeight: 1.5 }}>
        <b style={{ color: T.green }}>Who's set up to score.</b> Some defenses keep giving up touchdowns to one kind of
        player — the #2 receiver, the backup RB. <b style={{ color: T.text }}>Great matchup</b> = he's facing one of those soft spots.
      </div>
      <Tabs tabs={['Players', 'Defenses']} active={mode} onSelect={setMode} />
      {mode === 'Players' && all.length > 0 && <PosFilter value={pos} onChange={setPos} counts={counts} />}
      {mode === 'Players' ? (spots.length === 0 ? <Empty message={pos === 'ALL' ? 'No players to show yet this week.' : `No ${pos}s to show yet this week.`} /> : <>
        {great.length > 0 && <><SectionLabel>🎯 Great matchups · {great.length}</SectionLabel>
          {great.map(p => <SpotRow key={p.gsis_id} p={p} onPlayer={onPlayer} />)}</>}
        <SectionLabel>{great.length ? 'Everyone else' : 'This week'} · most likely to score first</SectionLabel>
        {rest.slice(0, 40).map(p => <SpotRow key={p.gsis_id} p={p} onPlayer={onPlayer} />)}
      </>) : (defs.length === 0 ? <Empty message="No defenses on this week's slate yet." /> : <>
        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>Pick a defense:</div>
        <ScrollRow style={{ paddingBottom: 10 }}>
          {defs.map(x => { const on = x === sel; return (
            <button key={x.team} onClick={() => setTeam(x.team)} style={{ flexShrink: 0, cursor: 'pointer', borderRadius: 20,
              padding: '6px 13px', fontSize: 12.5, fontWeight: 800, border: `1px solid ${on ? T.green : T.border}`,
              background: on ? T.greenDim : T.card, color: on ? T.green : T.muted, display: 'inline-flex', alignItems: 'center', gap: 5 }}><TeamLogo abbr={x.team} size={16} />{teamNick(x.team)}</button>
          ) })}
        </ScrollRow>
        <DefenseView d={sel} n={n} />
      </>)}
      <HowItWorks d={d} />
    </div>
  )
}
